import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import Loader from "../../../components/common/status/Loader/Loader";
import BlankLayout from "../../../layouts/BlankLayout";
import ErrorLoader from "../../../components/common/status/ErrorLoader/ErrorLoader";
import AssessmentBeginModal from "../sharedFiles/AssessmentBeginModal";
import {
  useGetAssessmentQuery,
  useGetCourseCoreQuery,
  useGetRunningAssessmentSubmissionByUserQuery,
} from "../../../graphql/generated/graphql";
import AssessmentCompleteModal from "../sharedFiles/AssessmentCompleteModal";
import ExitBeforeCompletedModal from "../sharedFiles/ExitBeforeCompletedModal";
import CursorLeavesWindowModal from "../sharedFiles/CursorLeavesWindowModal";
import AssessmentInProgressModal from "../sharedFiles/AssessmentInProgressModal";
import AssessmentErrorModal from "../sharedFiles/AssessmentErrorModal";
import {
  startSseAssessment,
  endSseAssessment,
  continueSseAssessment,
  getSseAssessmentTimer,
} from "../sharedFiles/AssessmentUtils";
import { Audio } from "expo-av";
import { Platform } from "react-native";
import QuestionCounter from "../sharedFiles/AssessmentQuestionCounter";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import {
  QUESTION_COUNTER_HEIGHT,
  SIMPLE_VOICE_ASSISTANT_HEIGHT_TURN_BASED,
  ASSESSMENT_MENU_HEIGHT,
  ASSESSMENT_MENU_HEIGHT_MOBILE,
  BOTTOM_SPACING_HEIGHT,
  CONTENT_CONTAINER_PADDING,
} from "../../../constants/assessmentConstants";
import AssessmentSpeakingOrb from "../sharedFiles/AssessmentSpeakingOrb";
import AssessmentMenu from "../../../components/navigation/AssessmentMenu/AssessmentMenu";
import { formatCountdownFromSeconds } from "../../../utils/timeUtils";
import { useTimerAnnouncements } from "../../../hooks/useTimerAnnouncements";
import TurnBasedAssessmentAudioRenderer from "./TurnBasedAssessmentAudioRenderer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { ActivityIndicator } from "react-native-paper";
import useTurnBasedAssessmentSSE from "../../../hooks/useTurnBasedAssessmentSSE";
import { Microphone } from "@phosphor-icons/react/dist/csr/Microphone";
import { usePreviewMode } from "../../../contexts/PreviewModeContext";

interface TurnBasedAssessmentProps {
  assessmentId: string;
  courseId: string;
  integrationId: string;
  moduleId?: string;
}

const TurnBasedAssessment = ({ assessmentId, courseId, integrationId, moduleId }: TurnBasedAssessmentProps) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [beginModalVisible, setBeginModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [anotherAssessmentInProgressModalVisible, setAnotherAssessmentInProgressModalVisible] = useState(false);
  const [cursorLeavesWindowModalVisible, setCursorLeavesWindowModalVisible] = useState(false);
  const [orbLoading, setOrbLoading] = useState(true);
  const [loadingAssessmentSubmission, setLoadingAssessmentSubmission] = useState(false);
  const [assessmentErrorModalVisible, setAssessmentErrorModalVisible] = useState(false);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [assessmentTimeLimit, setAssessmentTimeLimit] = useState<number | null>(null);
  const [assessmentNumberOfQuestions, setAssessmentNumberOfQuestions] = useState<number | null>(null);
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [introText, setIntroText] = useState<string | null>(null);
  const [introAudio, setIntroAudio] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recording, setRecording] = useState<Audio.Recording | null>(null);
  const [canAnswer, setCanAnswer] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [isAssessmentCompleting, setIsAssessmentCompleting] = useState(false);
  const [currentQuestionText, setCurrentQuestionText] = useState<string | null>(null);
  const [currentQuestionAudio, setCurrentQuestionAudio] = useState<string | null>(null);
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  const isIntroCompleteRef = useRef(false);
  const resetSequenceRef = useRef<(() => void) | null>(null);
  const audioQueueRef = useRef<{ seq: number; text: string; audio: string }[]>([]);
  const audioQueueCallbackRef = useRef<((chunk: { seq: number; text: string; audio: string }) => void) | null>(null);
  const processRemainingChunksRef = useRef<(() => void) | null>(null);
  const [displayTime, setDisplayTime] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastChunkAtRef = useRef<number | null>(null);
  const sseWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  const endTimeRef = useRef<number | null>(null);
  const assessmentIdRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const timerSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSyncingTimerRef = useRef<boolean>(false);
  const {
    announcement: timerAnnouncement,
    isAssertive: timerAnnouncementAssertive,
    checkAnnouncement,
    resetAnnouncements,
  } = useTimerAnnouncements();

  const [recordingAnnouncement, setRecordingAnnouncement] = useState<string>("");

  const { isMedium } = useResponsiveScreen();
  const { isPreviewMode } = usePreviewMode();
  const { height } = Dimensions.get("window");
  const PREVIEW_BANNER_HEIGHT = 50;
  const dynamicHeight = Math.max(
    5,
    height -
      QUESTION_COUNTER_HEIGHT -
      SIMPLE_VOICE_ASSISTANT_HEIGHT_TURN_BASED -
      (isMedium ? ASSESSMENT_MENU_HEIGHT : ASSESSMENT_MENU_HEIGHT_MOBILE) -
      BOTTOM_SPACING_HEIGHT -
      CONTENT_CONTAINER_PADDING * 2 -
      (isPreviewMode ? PREVIEW_BANNER_HEIGHT : 0)
  );

  const handleMicrophoneSelect = (deviceId: string) => {
    setSelectedMicrophoneId(deviceId);
  };

  const { refetch: refetchCourseData } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
  });

  const { data: assessment, refetch: refetchAssessment } = useGetAssessmentQuery({
    variables: {
      assessmentId: assessmentId,
    },
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: assessmentSubmission,
    loading: assessmentSubmissionLoading,
    error: assessmentSubmissionError,
    refetch: refetchAssessmentSubmission,
  } = useGetRunningAssessmentSubmissionByUserQuery({
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const assessmentSubmissionData = assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data;

  const assessmentSubmissionNotExistsInDatabase = !assessmentSubmissionData;

  const assessmentSubmissionIsRunningInDatabase = assessmentSubmissionData?.status === "RUNNING";

  const userAlreadyHasAnotherRunningAssessment =
    assessmentSubmissionData?.assessment?.id && assessmentSubmissionData?.assessment?.id !== assessmentId;

  const activeAssessmentId = assessmentSubmissionData?.assessment?.id;

  const assessmentIsRunning =
    !!activeAssessmentId && activeAssessmentId === assessmentId && assessmentSubmissionIsRunningInDatabase;

  const cleanupCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timerSyncIntervalRef.current) {
      clearInterval(timerSyncIntervalRef.current);
      timerSyncIntervalRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (sseWatchdogRef.current) {
      clearInterval(sseWatchdogRef.current);
      sseWatchdogRef.current = null;
    }
    endTimeRef.current = null;
    remainingSecondsRef.current = 0;
    timerStartTimeRef.current = null;
    retryCountRef.current = 0;
    assessmentIdRef.current = null;
    isSyncingTimerRef.current = false;
  }, []);

  const handleAssessmentComplete = useCallback(() => {
    cleanupCountdown();
    setLoadingAssessmentSubmission(false);
    setCompleteModalVisible(true);
    setDisplayTime("00:00");
  }, [cleanupCountdown]);

  const remainingSecondsRef = useRef<number>(0);
  const timerStartTimeRef = useRef<number | null>(null);

  const updateRemaining = useCallback(
    (remainingSec: number) => {
      setDisplayTime(formatCountdownFromSeconds(remainingSec));
      if (remainingSec <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        // Time is up, terminate the assessment
        endSseAssessment(() => {
          console.error("Error ending assessment on timeout");
        }).then(() => {
          handleAssessmentComplete();
        });
      }
    },
    [handleAssessmentComplete]
  );

  const syncTimerWithServer = useCallback(async () => {
    if (isSyncingTimerRef.current) {
      return;
    }
    isSyncingTimerRef.current = true;
    try {
      const timerData = await getSseAssessmentTimer();
      if (timerData && timerData.remainingSec !== undefined) {
        remainingSecondsRef.current = timerData.remainingSec;
        timerStartTimeRef.current = Date.now();
        updateRemaining(timerData.remainingSec);
      }
    } catch (error) {
      console.error("Error syncing timer with server:", error);
    } finally {
      isSyncingTimerRef.current = false;
    }
  }, [updateRemaining]);

  const startTimer = useCallback(
    (remainingSec: number) => {
      resetAnnouncements();
      remainingSecondsRef.current = remainingSec;
      timerStartTimeRef.current = Date.now();
      updateRemaining(remainingSec);
      checkAnnouncement(remainingSec);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (remainingSecondsRef.current > 0) {
          remainingSecondsRef.current = Math.max(0, remainingSecondsRef.current - 1);
          updateRemaining(remainingSecondsRef.current);
          checkAnnouncement(remainingSecondsRef.current);
        }
      }, 1000);

      if (timerSyncIntervalRef.current) {
        clearInterval(timerSyncIntervalRef.current);
      }
      timerSyncIntervalRef.current = setInterval(() => {
        syncTimerWithServer();
      }, 5_000);
    },
    [updateRemaining, syncTimerWithServer, checkAnnouncement, resetAnnouncements]
  );

  const handleVisibilityChange = useCallback(() => {
    if (!assessmentIsRunning) {
      return;
    }
    if (document.hidden) {
      setCursorLeavesWindowModalVisible(true);
    }
  }, [assessmentIsRunning]);

  const handleMouseLeave = useCallback(
    (event) => {
      if (!assessmentIsRunning) {
        return;
      }
      const { clientX, clientY } = event;

      const leftEdge = clientX <= 0;
      const rightEdge = clientX >= window.innerWidth;
      const topEdge = clientY <= 0;
      const bottomEdge = clientY >= window.innerHeight;

      if (leftEdge || rightEdge || topEdge || bottomEdge) {
        setCursorLeavesWindowModalVisible(true);
      }
    },
    [assessmentIsRunning]
  );

  const openSSE = useTurnBasedAssessmentSSE({
    eventSourceRef,
    isIntroCompleteRef,
    resetSequenceRef,
    audioQueueRef,
    audioQueueCallbackRef,
    processRemainingChunksRef,
    lastChunkAtRef,
    sseWatchdogRef,
    endTimeRef,
    intervalRef,
    assessmentIdRef,
    retryCountRef,
    setAssessmentErrorModalVisible,
    setQuestionIndex,
    setIsIntroComplete,
    setIntroText,
    setIntroAudio,
    setCurrentQuestionText,
    setCurrentQuestionAudio,
    setCanAnswer,
    setIsRecording,
    setIsProcessingAnswer,
    setDisplayTime,
    setRecording,
    recording,
    updateRemaining,
    handleAssessmentComplete,
    cleanupCountdown,
    handleVisibilityChange,
    handleMouseLeave,
  });

  const handleBeginAssessment = async (withRefetch = true) => {
    if (assessmentIsRunning) {
      return;
    }
    setLoadingAssessmentSubmission(true);
    setIntroText(null);
    setIntroAudio(null);
    setCurrentQuestionText(null);
    setCurrentQuestionAudio(null);
    setIsIntroComplete(false);
    isIntroCompleteRef.current = false;
    setCanAnswer(false);
    setQuestionIndex(0);
    setIsRecording(false);
    setIsProcessingAnswer(false);
    setIsAssessmentCompleting(false);
    if (recording) {
      recording.stopAndUnloadAsync().catch(console.error);
      setRecording(null);
    }
    audioQueueRef.current = [];
    audioQueueCallbackRef.current = null;
    const response = await startSseAssessment(assessmentId, handleSseAssessmentError);
    if (withRefetch) {
      await refetchAssessmentSubmission();
    }
    if (response?.startedAt && response?.durationSec) {
      const remainingSec = response.remainingSec ?? response.durationSec;
      startTimer(remainingSec);

      // Calculate endTime for SSE (still needed for SSE connection)
      const startTime =
        typeof response.startedAt === "number" ? response.startedAt : new Date(response.startedAt).getTime();
      const endTime = startTime + response.durationSec * 1000;
      openSSE(assessmentId, endTime);
    }
    setLoadingAssessmentSubmission(false);
    setBeginModalVisible(false);
  };

  useFocusEffect(
    useCallback(() => {
      const initializePageState = async () => {
        if (assessmentSubmissionLoading) {
          return;
        }

        if (userAlreadyHasAnotherRunningAssessment) {
          setAnotherAssessmentInProgressModalVisible(true);
          return;
        }

        if (assessmentSubmissionIsRunningInDatabase) {
          setLoadingAssessmentSubmission(true);
          // Sync timer with server on reload
          const timerData = await getSseAssessmentTimer();
          if (timerData) {
            const remainingSec = timerData.remainingSec ?? timerData.durationSec;
            startTimer(remainingSec);
            if (timerData.questionIndex !== undefined) {
              setQuestionIndex(timerData.questionIndex + 1);
            }
            if (timerData.introPlayed !== undefined) {
              setIsIntroComplete(timerData.introPlayed);
              isIntroCompleteRef.current = timerData.introPlayed;
            }
          }
          setIsRecording(false);
          setIsProcessingAnswer(false);
          setCanAnswer(false);
          if (recording) {
            recording.stopAndUnloadAsync().catch(console.error);
            setRecording(null);
          }
          setCurrentQuestionText(null);
          setCurrentQuestionAudio(null);
          setIntroText(null);
          setIntroAudio(null);
          if (resetSequenceRef.current) {
            resetSequenceRef.current();
          }
          audioQueueRef.current = [];
          const response = await startSseAssessment(assessmentId, handleSseAssessmentError);
          if (response?.startedAt && response?.durationSec) {
            const remainingSec = response.remainingSec ?? response.durationSec;
            startTimer(remainingSec);

            // Calculate endTime for SSE (still needed for SSE connection)
            const startTime =
              typeof response.startedAt === "number" ? response.startedAt : new Date(response.startedAt).getTime();
            const endTime = startTime + response.durationSec * 1000;
            openSSE(assessmentId, endTime);
          }
          setLoadingAssessmentSubmission(false);
          return;
        }

        const showBeginModal = assessmentSubmissionNotExistsInDatabase && !activeAssessmentId;
        if (showBeginModal) {
          setBeginModalVisible(true);
          return;
        }
      };

      if (!assessmentSubmissionLoading) {
        initializePageState();
      }
    }, [assessmentId, assessmentSubmissionLoading, assessmentSubmission])
  );

  const handleSseAssessmentError = () => {
    setLoadingAssessmentSubmission(false);
    setBeginModalVisible(false);
    setAssessmentErrorModalVisible(true);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("mouseleave", handleMouseLeave);
  };

  useFocusEffect(
    useCallback(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [assessmentIsRunning])
  );

  useFocusEffect(
    useCallback(() => {
      if (!assessmentId) {
        return;
      }
      refetchAssessment();
      refetchAssessmentSubmission();
    }, [assessmentId])
  );

  useFocusEffect(
    useCallback(() => {
      if (assessment?.getAssessment?.data) {
        const timeLimitMinutes = assessment.getAssessment.data.timeLimit;
        const timeLimitSeconds = timeLimitMinutes * 60;
        setAssessmentTimeLimit(timeLimitMinutes);
        setAssessmentNumberOfQuestions(assessment.getAssessment.data.numberOfQuestions);
        setDisplayTime(formatCountdownFromSeconds(timeLimitSeconds));
      }
    }, [assessment?.getAssessment?.data])
  );

  useEffect(() => {
    return () => {
      cleanupCountdown();
    };
  }, [cleanupCountdown]);

  if (assessmentSubmissionLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }
  if (assessmentSubmissionError || !assessment?.getAssessment?.data) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </BlankLayout>
    );
  }
  const assessmentHasNoQuestions = assessment?.getAssessment?.data?.aiTutors?.length === 0;
  if (assessmentHasNoQuestions) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="This assessment is not enabled." />
        </View>
      </BlankLayout>
    );
  }

  const handleExitPage = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      if (moduleId) {
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: courseId,
          integration_id: typeof integrationId === "string" ? integrationId : courseId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: courseId,
          integration_id: integrationId,
        });
      }
    }
  };

  const handleCancelAssessmentBegin = () => {
    setBeginModalVisible(false);
    handleExitPage();
  };

  const handleConfirmCompleteModal = () => {
    setCompleteModalVisible(false);
    refetchCourseData();
    const timeLimitSeconds = assessmentTimeLimit ? assessmentTimeLimit * 60 : 0;
    setDisplayTime(formatCountdownFromSeconds(timeLimitSeconds));
    handleExitPage();
  };

  const handleTerminateAssessment = async () => {
    if (!assessmentId) {
      return;
    }
    setExitModalVisible(false);

    cleanupCountdown();

    await endSseAssessment(() => {
      console.error("Error ending assessment on server");
    });

    setLoadingAssessmentSubmission(false);

    await refetchCourseData();
    handleExitPage();
  };

  const handleAnotherAssessmentInProgressModalClick = () => {
    setAnotherAssessmentInProgressModalVisible(false);
    handleExitPage();
  };

  const handleExitButtonPress = () => {
    if (assessmentIsRunning) {
      setExitModalVisible(true);
    } else {
      handleExitPage();
    }
  };

  const handleAssessmentErrorModalClick = () => {
    setAssessmentErrorModalVisible(false);
    handleExitPage();
  };

  const startRecording = async () => {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const { recording: expoAvRecording } = await Audio.Recording.createAsync({
        ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
        web: {
          mimeType: isSafari ? "video/mp4" : "audio/webm",
          bitsPerSecond: 128000,
        },
      });
      setRecording(expoAvRecording);
      setIsRecording(true);
      setRecordingAnnouncement("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = async () => {
    if (!recording) {
      return;
    }

    setIsProcessingAnswer(true);
    setCanAnswer(false);

    await recording.stopAndUnloadAsync();
    const uri = recording.getURI();
    setRecording(null);
    setIsRecording(false);
    setRecordingAnnouncement("Recording stopped");

    try {
      let fileString;

      if (Platform.OS === "web") {
        const response = await fetch(uri);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          fileString = (reader.result as string).split(",")[1]; // Get the Base64 content
          await sendAnswerToServer(fileString);
        };
      } else {
        const FileSystem = require("expo-file-system");
        fileString = await FileSystem.readAsStringAsync(uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        await sendAnswerToServer(fileString);
      }
    } catch (error) {
      console.error("Error stopping recording:", error);
      setIsProcessingAnswer(false);
      setCanAnswer(true);
    }
  };

  const sendAnswerToServer = async (audioBase64: string) => {
    try {
      setCurrentQuestionText(null);
      setCurrentQuestionAudio(null);
      if (resetSequenceRef.current) {
        resetSequenceRef.current();
      }
      audioQueueRef.current = [];

      const result = await continueSseAssessment(
        assessmentId,
        audioBase64,
        () => {
          console.error("Error continuing assessment");
          setIsProcessingAnswer(false);
          setCanAnswer(true);
        },
        (chunk) => {
          // Handle chunk event
          setCurrentQuestionText((prev) => (prev ? `${prev}${chunk.text}` : chunk.text));
          if (audioQueueCallbackRef.current) {
            audioQueueCallbackRef.current(chunk);
          } else {
            audioQueueRef.current.push(chunk);
            setCurrentQuestionAudio(chunk.audio);
          }
          lastChunkAtRef.current = Date.now();
        },
        (data) => {
          // Handle state_update event
          if (data.questionIndex !== undefined) {
            setQuestionIndex(data.questionIndex + 1);
          }
          if (data.awaitingAnswer) {
            setCanAnswer(true);
            setIsProcessingAnswer(false);
          }
        },
        () => {
          // Handle done event
          if (audioQueueCallbackRef.current) {
            while (audioQueueRef.current.length > 0) {
              const queued = audioQueueRef.current.shift();
              if (queued) {
                audioQueueCallbackRef.current(queued);
              }
            }
          }
          if (processRemainingChunksRef.current) {
            processRemainingChunksRef.current();
          }
          setTimeout(() => {
            setCanAnswer(true);
            setIsProcessingAnswer(false);
          }, 1000);
        },
        () => {
          setCanAnswer(true);
          setIsProcessingAnswer(false);
        }
      );

      if (result.completed) {
        setIsAssessmentCompleting(true);
        setIsProcessingAnswer(false);
        setCanAnswer(false);
        handleAssessmentComplete();
      }
    } catch (error) {
      console.error("Error sending answer to server:", error);
      setIsProcessingAnswer(false);
      setCanAnswer(true);
    }
  };

  const handleAnswer = async () => {
    if (isRecording) {
      await stopRecording();
    } else if (canAnswer && !isProcessingAnswer) {
      await startRecording();
    }
  };

  const showAssessmentAsRunningOrLoading =
    !assessmentErrorModalVisible &&
    !anotherAssessmentInProgressModalVisible &&
    !beginModalVisible &&
    !completeModalVisible;

  const moduleName = assessment?.getAssessment?.data?.moduleHasAssessment?.title;

  return (
    <BlankLayout>
      <View style={styles.mainContainer}>
        {/* @ts-expect-error -- role="heading" and aria-level are web-only ARIA props not in RN types */}
        <View
          style={styles.srOnly}
          role="heading"
          aria-level={1}
          accessibilityRole="header"
          accessibilityLabel={moduleName ? `Assessment: ${moduleName}` : "Assessment"}>
          <CustomText
            text={moduleName ?? "Assessment"}
            useTranslationText={false}
            accessibilityElementsHidden={false}
          />
        </View>
        <View
          style={styles.srOnly}
          role="status"
          aria-live="assertive"
          aria-atomic={true}
          accessibilityLiveRegion="assertive"
          accessible={true}>
          <CustomText
            text={recordingAnnouncement}
            useTranslationText={false}
            accessibilityLabel={recordingAnnouncement}
          />
        </View>
        <AssessmentMenu
          handleExitPress={handleExitButtonPress}
          remainingTime={displayTime}
          moduleName={moduleName}
          hasMicrophoneControls={true}
          timeIsLoading={!displayTime || !assessmentTimeLimit}
          isSseAssessment={true}
          selectedDeviceId={selectedMicrophoneId}
          onDeviceSelect={handleMicrophoneSelect}
          timerAnnouncement={timerAnnouncement}
          timerAnnouncementAssertive={timerAnnouncementAssertive}
        />
        <View style={styles.contentContainer}>
          {showAssessmentAsRunningOrLoading && (
            <View style={styles.orbContainer}>
              <AssessmentSpeakingOrb
                dynamicHeight={dynamicHeight}
                orbLoading={orbLoading}
                setOrbLoading={setOrbLoading}
                useLiveKit={false}
                isPlaying={isIntroPlaying}
                audioLevel={audioLevel}
              />
            </View>
          )}
          {loadingAssessmentSubmission && (
            <View style={{ height: 10, justifyContent: "center", alignItems: "center" }}>
              <ActivityIndicator size="large" color="#6B7280" accessibilityLabel="Preparing assessment" />
              <CustomText text="Preparing assessment..." style={{ color: "#666" }} useTranslationText={false} />
            </View>
          )}
          <View style={styles.overlayContent}>
            <QuestionCounter questionIndex={questionIndex} totalQuestions={assessmentNumberOfQuestions} />
            {(assessmentIsRunning || introText || introAudio || currentQuestionText || currentQuestionAudio) && (
              <TurnBasedAssessmentAudioRenderer
                introText={isIntroComplete ? currentQuestionText || undefined : introText || undefined}
                introAudio={isIntroComplete ? currentQuestionAudio || undefined : introAudio || undefined}
                onIntroStart={() => setIsIntroPlaying(true)}
                onIntroComplete={() => setIsIntroPlaying(false)}
                useStreaming={true}
                onAudioLevel={(lvl) => setAudioLevel(lvl)}
                onAudioQueueReady={(callback) => {
                  audioQueueCallbackRef.current = callback;
                  while (audioQueueRef.current.length > 0) {
                    const queued = audioQueueRef.current.shift();
                    if (queued) {
                      callback(queued);
                    }
                  }
                }}
                onStreamDone={(processRemaining) => {
                  processRemainingChunksRef.current = processRemaining;
                }}
                onResetSequence={(resetFn) => {
                  resetSequenceRef.current = resetFn;
                }}
              />
            )}
          </View>
          {!beginModalVisible && (
            <CustomButton
              text={
                isAssessmentCompleting
                  ? "Ending..."
                  : isProcessingAnswer
                    ? "Processing..."
                    : isRecording
                      ? "Stop recording"
                      : canAnswer
                        ? "Press to answer"
                        : "Waiting for question..."
              }
              onPress={handleAnswer}
              disabled={!canAnswer || isProcessingAnswer || isAssessmentCompleting}
              leftIcon={
                isAssessmentCompleting || isProcessingAnswer ? (
                  <View style={{ transform: [{ scale: 0.7 }] }}>
                    <ActivityIndicator size={20} color="#666" />
                  </View>
                ) : isRecording ? (
                  <Microphone size={20} weight="fill" color="#666" />
                ) : canAnswer ? (
                  <Microphone size={20} weight="regular" color="#666" />
                ) : (
                  <View style={{ transform: [{ scale: 0.7 }] }}>
                    <ActivityIndicator size={20} color="#666" />
                  </View>
                )
              }
              textStyle={{ color: "#666" }}
            />
          )}
          <View style={{ height: BOTTOM_SPACING_HEIGHT }} />
        </View>
      </View>

      <AssessmentBeginModal
        visible={beginModalVisible}
        onCancel={handleCancelAssessmentBegin}
        onConfirm={handleBeginAssessment}
        defaultTime={assessmentTimeLimit}
        defaultQuestionsCount={assessmentNumberOfQuestions}
        isLoading={false}
        selectedDeviceId={selectedMicrophoneId}
        onDeviceSelect={handleMicrophoneSelect}
        skipLivekitCheck={true}
      />
      <AssessmentCompleteModal visible={completeModalVisible} onConfirm={handleConfirmCompleteModal} />
      <ExitBeforeCompletedModal
        visible={exitModalVisible}
        onConfirm={handleTerminateAssessment}
        onCancel={() => setExitModalVisible(false)}
      />
      <CursorLeavesWindowModal
        visible={cursorLeavesWindowModalVisible}
        onConfirm={() => setCursorLeavesWindowModalVisible(false)}
      />
      <AssessmentInProgressModal
        visible={anotherAssessmentInProgressModalVisible}
        onConfirm={handleAnotherAssessmentInProgressModalClick}
      />
      <AssessmentErrorModal visible={assessmentErrorModalVisible} onConfirm={handleAssessmentErrorModalClick} />
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: CONTENT_CONTAINER_PADDING,
    paddingBottom: CONTENT_CONTAINER_PADDING,
    paddingTop: 0,
    backgroundColor: "transparent",
    position: "relative",
  },
  orbContainer: {
    position: "absolute",
    bottom: CONTENT_CONTAINER_PADDING,
    left: CONTENT_CONTAINER_PADDING,
    right: CONTENT_CONTAINER_PADDING,
    top: 0,
    justifyContent: "flex-start",
    alignItems: "center",
    zIndex: 1,
    paddingTop: CONTENT_CONTAINER_PADDING,
    pointerEvents: "none",
  },
  overlayContent: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mainContainer: {
    flex: 1,
    backgroundColor: "#E1E5FA",
  },
  srOnly: {
    position: "absolute",
    left: -10000,
    top: "auto",
    width: 1,
    height: 1,
    overflow: "hidden",
  },
});

export default TurnBasedAssessment;
